<template>
  <StandardCardPageContainer
    :include-navigation="tab == 'new-questionnaires' && !loadingPage"
  >
    <LoadingCircle v-if="loadingPage" />

    <template v-else>
      <v-tabs v-model="tab" class="mb-10">
        <v-tab
          v-if="authenticationStore.canCreateQuestionnaires()"
          value="my-questionnaires"
        >
          My Questionnaires
        </v-tab>
        <v-tab value="started-questionnaires"
          >Questionnaires in progress/completed
        </v-tab>
        <v-tab value="new-questionnaires">
          New questionnaires
          {{
            newQuestionnaires.length > 0 ? `(${newQuestionnaires.length})` : ""
          }}
        </v-tab>
      </v-tabs>
      <v-row justify="center">
        <v-col cols="12" class="text-center">
          <v-window v-model="tab">
            <v-window-item
              v-if="authenticationStore.canCreateQuestionnaires()"
              value="my-questionnaires"
            >
              <MyQuestionnairesView
                :questionnaires="questionnaireStore.sentQuestionnaires"
              />
            </v-window-item>
            <v-window-item value="started-questionnaires">
              <ReceivedQuestionnairesView
                :received-questionnaires="startedQuestionnaires"
                :choose-multiple="false"
              />
            </v-window-item>
            <v-window-item value="new-questionnaires">
              <ReceivedQuestionnairesView
                :received-questionnaires="newQuestionnaires"
                :choose-multiple="true"
              />
            </v-window-item>
          </v-window>
        </v-col>
      </v-row>
    </template>
    <template #nextButton>
      <PActionButton
        :append-icon="true"
        :icon-size="'xs'"
        appkit-icon="icon-right-chevron-fill"
        :disabled="selectedQuestionnaireStore.questionnaires.length < 1"
        @click="routeToAnswering"
        >Next
      </PActionButton>
    </template>
  </StandardCardPageContainer>
</template>

<script setup lang="ts">
import LoadingCircle from "@/components/custom/LoadingCircle.vue";
import { genericError } from "@/modules/utilities";
import { onBeforeMount, ref, type Ref } from "vue";
import StandardCardPageContainer from "@/components/containers/StandardCardPageContainer.vue";
import MyQuestionnairesView from "@/views/questionnaires/my-questionnaires/MyQuestionnairesView.vue";
import ReceivedQuestionnairesView from "@/views/questionnaires/ReceivedQuestionnairesView.vue";
import { useSnackBarStore } from "@/stores/snackBarStore";
import { useAuthenticationStore } from "@/stores/authentication";
import { useQuestionnairesStore } from "@/stores/questionnaires";
import { useSelectedQuestionnaire } from "@/stores/selectedQuestionnare";
import { computed } from "vue";
import { AnswerStatus } from "@/models/enum/answerStatus";
import PActionButton from "@/components/custom/buttons/PActionButton.vue";
import { useRouter } from "vue-router";
import { RouterPath } from "@/models/enum/routerPath";
import { getCompletedReceivedQuestionnairesForTemplateId } from "@/services/questionnairesService";

const props = defineProps<{
  initialTab?: string;
}>();

const authenticationStore = useAuthenticationStore();
const snackbarStore = useSnackBarStore();
const questionnaireStore = useQuestionnairesStore();
const selectedQuestionnaireStore = useSelectedQuestionnaire();
const router = useRouter();

const loadingPage = ref(true);
const tab: Ref<string | null> = ref(props.initialTab ?? null);

onBeforeMount(async () => {
  await fetchData();
});

const fetchData = async () => {
  try {
    loadingPage.value = true;
    await questionnaireStore.getSentAndReceivedQuestionnaires();
  } catch (error) {
    snackbarStore.showError(genericError);
  } finally {
    loadingPage.value = false;
  }
};

const startedQuestionnaires = computed(() => {
  return questionnaireStore.receivedQuestionnaires.questionnaires.filter(
    (q) => q.answerStatus != AnswerStatus.NotStarted
  );
});

const newQuestionnaires = computed(() => {
  return questionnaireStore.receivedQuestionnaires.questionnaires.filter(
    (q) => q.answerStatus == AnswerStatus.NotStarted
  );
});

const routeToStartQuestionnaire = () =>
  router.push(RouterPath.StartQuestionnaire);
const routeToReuseAnswers = () => router.push(RouterPath.ReuseAnswers);

async function routeToAnswering() {
  const completedAnswers =
    await getCompletedReceivedQuestionnairesForTemplateId(
      selectedQuestionnaireStore.questionnaire.templateId
    );

  selectedQuestionnaireStore.questionnaire.answerStatus ===
    AnswerStatus.NotStarted && completedAnswers.length > 0
    ? routeToReuseAnswers()
    : routeToStartQuestionnaire();
}
</script>

<style>
.v-icon:hover {
  cursor: default;
}
</style>
